import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
  type ReactNode,
} from 'react';
import { client, type SessionUser } from './api';

type AuthState = {
  user: SessionUser | null;
  /** Not signed in, but browsing from an allowed network. */
  guest: boolean;
  loading: boolean;
  refresh: () => Promise<void>;
  login: (email: string, password: string) => Promise<SessionUser>;
  logout: () => Promise<void>;
};

const AuthContext = createContext<AuthState | null>(null);

export function AuthProvider({ children }: { children: ReactNode }) {
  const [user, setUser] = useState<SessionUser | null>(null);
  const [guest, setGuest] = useState(false);
  const [loading, setLoading] = useState(true);

  const refresh = useCallback(async () => {
    try {
      const { user: me, guest: g } = await client.me();
      setUser(me);
      setGuest(Boolean(g) && !me);
    } catch {
      setUser(null);
      setGuest(false);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    void refresh();
  }, [refresh]);

  const login = useCallback(async (email: string, password: string) => {
    const { user: me } = await client.login(email, password);
    setUser(me);
    setGuest(false);
    return me;
  }, []);

  const logout = useCallback(async () => {
    await client.logout();
    setUser(null);
    await refresh();
  }, [refresh]);

  const value = useMemo(
    () => ({ user, guest, loading, refresh, login, logout }),
    [user, guest, loading, refresh, login, logout],
  );

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth() {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error('useAuth must be used within AuthProvider');
  return ctx;
}
